import { cn } from "@aurem/ui";
import { useLocalStorage } from "@aurem/utils";
import type { HTMLAttributes, JSX, PropsWithChildren, ReactNode } from "react";
import { createContext, use } from "react";
import { Link, useMatch, useResolvedPath } from "react-router-dom";
import { useTranslations } from "./i18n";
import { SidebarFilled } from "./icons/sidebar-filled";

const COLLAPSED_STORAGE_KEY = "innerSidebarCollapsed";

const CollapsedContext = createContext(false);

export type InnerSidebarProps = {
  children: ReactNode;
  header: JSX.Element;
  collapsedLogo?: ReactNode;
  footer?: JSX.Element;
  collapsible?: boolean;
  /** @deprecated */
  setSeparator?: boolean;
};

export const InnerSidebar = ({
  children,
  header,
  collapsedLogo,
  footer,
  collapsible = true,
}: InnerSidebarProps) => {
  const { t } = useTranslations();
  const [collapsed, setCollapsed] = useLocalStorage(
    COLLAPSED_STORAGE_KEY,
    false,
  );
  const isCollapsed = collapsible && Boolean(collapsed);

  const toggleIcon = <SidebarFilled size={20} className="rtl:-scale-x-100" />;

  return (
    <CollapsedContext value={isCollapsed}>
      <aside
        className={cn(
          "hidden sm:flex flex-col shrink-0 overflow-hidden",
          "bg-secondary-500 text-white",
          "transition-[width] duration-200",
          isCollapsed ? "w-[4.5rem]" : "w-[12.5rem]",
        )}
      >
        <div
          className={cn(
            "flex h-16 shrink-0 items-center overflow-hidden border-b border-white/10",
            !isCollapsed && "gap-1 pe-3",
          )}
        >
          {isCollapsed ? (
            <button
              type="button"
              data-e2e-id="inner_sidebar_toggle"
              aria-expanded={false}
              aria-label={t("components.innerSidebar.expand")}
              onClick={() => setCollapsed(false)}
              className={cn(
                "group relative flex h-16 w-full items-center justify-center",
                "text-white/70 transition-colors hover:text-white",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-white",
              )}
            >
              {!!collapsedLogo && (
                <span className="transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0">
                  {collapsedLogo}
                </span>
              )}
              <span
                className={cn(
                  "flex items-center justify-center",
                  !!collapsedLogo &&
                    "absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100",
                )}
              >
                {toggleIcon}
              </span>
            </button>
          ) : (
            <>
              <div className="flex-1 min-w-0">{header}</div>
              {collapsible && (
                <button
                  type="button"
                  data-e2e-id="inner_sidebar_toggle"
                  aria-expanded
                  aria-label={t("components.innerSidebar.collapse")}
                  onClick={() => setCollapsed(true)}
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                    "bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white",
                  )}
                >
                  {toggleIcon}
                </button>
              )}
            </>
          )}
        </div>
        <ul className="flex-1 overflow-y-auto space-y-1 p-3">{children}</ul>
        <ul className="space-y-1 p-3">{footer}</ul>
      </aside>
    </CollapsedContext>
  );
};

export type HeaderProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>>;

export const Header = ({ className, ...props }: HeaderProps) => {
  return (
    <div
      className={cn(
        "h-16 w-full flex items-center justify-between shrink-0 gap-2 px-3 py-1.5",
        className,
      )}
      {...props}
    ></div>
  );
};

export type InnerSidebarItemProps = {
  label: string;
  icon?: ReactNode;
  children?: typeof InnerSidebarItem | (typeof InnerSidebarItem)[];
  /** Renders a <Link> when set. Mutually exclusive with `onClick`. */
  to?: string;
  /** Renders a <button> when set and `to` is omitted. */
  onClick?: () => void;
  /** Path pattern for detecting whether the link is active; resolved to is used by default */
  match?: string;
  e2eId?: string;
  className?: string;
};

const itemClassName = (
  isSelected: boolean,
  isCollapsed: boolean,
  extra?: string,
) =>
  cn(
    "flex items-center rounded-full text-sm transition-colors",
    isCollapsed
      ? "mx-auto h-10 w-10 justify-center"
      : "w-full gap-3 px-3 py-2.5 text-start",
    "focus-visible:outline focus-visible:outline-1 focus-visible:outline-white focus-visible:outline-offset-2",
    isSelected
      ? "bg-white text-secondary-500 font-medium"
      : "text-white/70 hover:bg-white/10 hover:text-white aria-pressed:bg-white/20 active:bg-white/20",
    extra,
  );

export const InnerSidebarItem = ({
  label,
  icon,
  to,
  onClick,
  match,
  e2eId,
  className,
}: InnerSidebarItemProps) => {
  const isCollapsed = use(CollapsedContext);
  match ??= to ?? "";
  const resolved = useResolvedPath(match);
  const routeMatch = useMatch({ path: resolved.pathname, end: false });
  const isSelected = !!to && !!routeMatch;
  const iconNode = icon ? (
    <span className="shrink-0 rtl:-scale-x-100">{icon}</span>
  ) : null;
  const content =
    isCollapsed && iconNode ? (
      iconNode
    ) : (
      <>
        {iconNode}
        <span className="truncate">{label}</span>
      </>
    );
  return (
    <li className="list-none">
      {to ? (
        <Link
          data-e2e-id={e2eId}
          to={to}
          title={label}
          aria-label={isCollapsed ? label : undefined}
          className={itemClassName(isSelected, isCollapsed, className)}
        >
          {content}
        </Link>
      ) : (
        <button
          type="button"
          data-e2e-id={e2eId}
          onClick={onClick}
          title={label}
          aria-label={isCollapsed ? label : undefined}
          className={itemClassName(false, isCollapsed, className)}
        >
          {content}
        </button>
      )}
    </li>
  );
};
